import type { QuestId } from './types'

/**
 * The weekly-quest catalog and the schedule that surfaces 2 of them per gameweek.
 *
 * **Shared, not client code**. It lives under `src/` because the guard rail in
 * `server/football/__tests__/guardRails.test.ts` forbids the client importing anything from
 * `server/` — the dependency can only point this way. The server imports it exactly as it already
 * imports `src/types.ts` and `src/teams.ts`, so both sides answer "which quests ran in GW n?" from
 * one function rather than from two copies that drift.
 *
 * Was `src/mockQuests.ts`. Nothing in here is mock: the definitions are the real
 * catalog and the schedule is the real schedule. The results were the last fabricated part, and
 * real grading replaced them — see `src/questResults.ts`.
 */

export interface QuestDefinition {
  id: QuestId
  name: string
  question: string
  type: 'team' | 'match' | 'over_under'
  /** Shown in cta/confirm next to the quest name — locked shows the LOCKED
   * marker instead (see QuestCard). */
  tag: string
  /** over/under only, e.g. 2.5 — fixed per the quest definition, never
   * player-editable. */
  line?: number
  /** over/under only, e.g. "GW PENALTIES" */
  eyebrow?: string
}

// The 8-quest pool from the brief. 2 of these surface per gameweek — see
// buildQuestPairSchedule below. This array's ORDER is load-bearing: it, and not the order the
// operator happened to type ids into QUESTS_ENABLED, is what the generated schedule iterates.
//
export const questDefinitions: QuestDefinition[] = [
  {
    id: 'busiestKeeper',
    name: 'Busiest Keeper',
    question: 'Most saves this GW',
    type: 'team',
    tag: 'TEAM PICK',
  },
  {
    id: 'bookingBandits',
    name: 'Booking Bandits',
    question: 'Most cards this GW',
    type: 'team',
    tag: 'TEAM PICK',
  },
  {
    id: 'frontRunners',
    name: 'Front Runners',
    // The rule decision: a boolean per pick, not a single winner. Six or seven teams
    // lead at half-time in a normal round, so "the team leading at half-time" has no one answer —
    // the copy says whose team, because that is what is graded.
    question: 'Your team leads at HT',
    type: 'team',
    tag: 'TEAM PICK',
  },
  {
    id: 'cornerStorm',
    name: 'Corner Storm',
    question: 'Match with most corners',
    type: 'match',
    tag: 'MATCH PICK',
  },
  {
    id: 'secondHalfSurge',
    name: 'Second-Half Surge',
    question: 'Most 2nd-half goals',
    type: 'match',
    tag: 'MATCH PICK',
  },
  {
    id: 'firstHalfAction',
    name: 'First-Half Action',
    question: 'Over 1.5 first-half goals',
    type: 'match',
    tag: 'MATCH PICK',
  },
  {
    id: 'spotKickLine',
    name: 'Spot-Kick Line',
    // "Taken", not "awarded": the feed has no penalty-awarded event, and awarded
    // could only ever be approximated. Scored + missed penalties is exactly what it does have.
    question: '2.5 penalties taken',
    type: 'over_under',
    tag: 'OVER/UNDER',
    line: 2.5,
    eyebrow: 'GW PENALTIES',
  },
  {
    id: 'braceBracket',
    name: 'Brace Bracket',
    // A brace is two goals **or more**, so a hat-trick contains one.
    question: 'Braces across the GW',
    type: 'over_under',
    tag: 'OVER/UNDER',
    line: 2.5,
    eyebrow: 'GW BRACES',
  },
]

export const questById: Record<QuestId, QuestDefinition> = Object.fromEntries(
  questDefinitions.map((quest) => [quest.id, quest])
) as Record<QuestId, QuestDefinition>

/** Every known quest id, in catalog order. */
export const QUEST_IDS: QuestId[] = questDefinitions.map((quest) => quest.id)

export function isQuestId(value: string): value is QuestId {
  return Object.hasOwn(questById, value)
}

/**
 * Two is the floor for the feature to exist at all: a gameweek shows a *pair*, and a pool of one
 * can only pair a quest with itself. One enabled quest is therefore off, not "one quest a week".
 *
 */
export const MIN_ENABLED_QUESTS = 2

export function questsAreAvailable(enabled: readonly QuestId[]): boolean {
  return enabled.length >= MIN_ENABLED_QUESTS
}

export class QuestConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'QuestConfigError'
  }
}

/**
 * Parses `QUESTS_ENABLED`.
 *
 * - unset / empty → **all eight**, so landing this ticket changes no behaviour
 * - `none` → the shelf switch, no quests anywhere
 * - otherwise a comma-separated id list, order-insensitive (the result is in catalog order)
 *
 * An unknown id throws rather than being skipped. A typo that silently disables a quest is the
 * exact failure mode the flag exists to prevent, and it would be invisible until a matchday.
 */
export function parseQuestsEnabled(raw: string | undefined): QuestId[] {
  const trimmed = raw?.trim()
  if (trimmed === undefined || trimmed === '') return [...QUEST_IDS]
  if (trimmed.toLowerCase() === 'none') return []

  const requested = trimmed
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')

  const unknown = requested.filter((id) => !isQuestId(id))
  if (unknown.length > 0) {
    throw new QuestConfigError(
      `QUESTS_ENABLED names unknown quest ${unknown.length > 1 ? 'ids' : 'id'} ` +
        `${unknown.map((id) => JSON.stringify(id)).join(', ')} ` +
        `(known: ${QUEST_IDS.join(', ')}; or 'none' to disable quests entirely)`
    )
  }

  // Catalog order, deduplicated: the schedule must not change because someone rewrote the
  // variable with the same contents in a different order.
  const requestedSet = new Set(requested)
  return QUEST_IDS.filter((id) => requestedSet.has(id))
}

/** Stand-in for the odd-n bye — one quest sits out each round rather than pairing. */
const BYE = '__bye__'

/**
 * The round-robin (circle method / 1-factorization) pair schedule over the enabled quests.
 *
 * Every unordered pair exactly once: C(n,2) pairs across n-1 rounds of ⌊n/2⌋ disjoint pairs. Odd n
 * pads with a bye, so each quest sits out exactly one round and the total is C(n,2) all the same.
 *
 * This used to be 28 rows pinned as seed data, which made "turn one quest off" impossible: drop a
 * quest and 7 of the 28 rows reference something that no longer exists. The pinned
 * table survives as a test fixture — generating over all eight must reproduce it row for row.
 */
export function buildQuestPairSchedule(
  enabled: readonly QuestId[]
): [QuestId, QuestId][] {
  const ordered = QUEST_IDS.filter((id) => enabled.includes(id))
  if (!questsAreAvailable(ordered)) return []

  const carousel: (QuestId | typeof BYE)[] =
    ordered.length % 2 === 0 ? [...ordered] : [...ordered, BYE]
  const n = carousel.length

  const pairs: [QuestId, QuestId][] = []
  for (let round = 0; round < n - 1; round++) {
    for (let i = 0; i < n / 2; i++) {
      const a = carousel[i]
      const b = carousel[n - 1 - i]
      if (a !== BYE && b !== BYE) pairs.push([a, b])
    }
    // Rotate every position but the first: the fixed head plus a rotating tail is what makes
    // each round a different perfect matching.
    carousel.splice(1, 0, carousel.pop()!)
  }
  return pairs
}

/**
 * The quests that surface for a given gameweek (1-indexed) — a pair, or none at all.
 *
 * Pure function of `(enabled, gameweek)`, so every client and the server agree without anyone
 * sending the answer over the wire. Wraps: gameweek n takes pair `(n-1) % pairs.length`, unchanged
 * from the pinned schedule's behaviour.
 */
export function getQuestPairForGameweek(
  gameweek: number,
  enabled: readonly QuestId[]
): [QuestId, QuestId] | null {
  const pairs = buildQuestPairSchedule(enabled)
  if (pairs.length === 0) return null
  // Guards a gameweek 0 / negative from indexing off the front of the array.
  const index = (((gameweek - 1) % pairs.length) + pairs.length) % pairs.length
  return pairs[index]
}

/** The pair as full definitions, or `[]` when quests are off for this deployment. */
export function getQuestsForGameweek(
  gameweek: number,
  enabled: readonly QuestId[]
): QuestDefinition[] {
  const pair = getQuestPairForGameweek(gameweek, enabled)
  return pair === null ? [] : [questById[pair[0]], questById[pair[1]]]
}
